import { create } from 'zustand'
import { readFirebaseConfig } from './config'

/**
 * What drivers have said about whether an unconfirmed station is really there.
 *
 * The pipeline can say *why* a site could not be confirmed; only somebody
 * standing in front of it can say whether it is there. This is the first piece of
 * state in the product that users write rather than read, and it is deliberately
 * behind an interface: the panel is built and reviewable before the Firestore
 * collection, the anonymous auth dependency and the security rules land.
 *
 * ### What one vote is worth
 *
 * Not always the same thing. Anonymous auth makes one browser one voice, which
 * `firestore.rules` enforces - but a browser is free. A private window is a
 * browser, and so is every profile in Chrome, so a bar of five yeses was five
 * clicks by one determined person, and the result is shown to drivers as fact
 * about whether a charger exists.
 *
 * Three things stand between that person and a cleared warning:
 *
 *  - **weight** - a browser voting for the first time counts {@link FIRST_VOTE_WEIGHT},
 *    one that has been voting for a week counts {@link ESTABLISHED_VOTE_WEIGHT}.
 *    Costs *forethought*: profiles have to be made and then left alone.
 *  - **the score** - {@link CONFIRMED_AT} of weighted vote. Costs *volume*.
 *  - **the spread** - the answers have to land on {@link SETTLED_OVER_DAYS}
 *    different calendar days. Costs *patience*, which is the one thing a script
 *    cannot compress: two days is two days.
 *
 * ### What the numbers are, and are not, for
 *
 * `tools/confirmation-sim.mjs` is the arithmetic behind every constant below, and
 * its first finding is the one that shaped them: **none of this is sybil
 * resistance.** A fresh anonymous uid is free and unlimited, so a score threshold
 * is bought with a `for` loop. Simulated against the 1445 unlisted sites, a
 * pre-aged pool of seven browser profiles flips the entire layer inside the
 * spread window at any threshold in the range worth considering - and raising the
 * bar from 5 to 12 made a national sweep *cheaper*, because the extra calendar
 * days it demanded handed the sweeper more working hours than the extra votes
 * cost them. What stops a scripted caller is App Check; see `later.md`.
 *
 * What these numbers do decide is how much of the layer the vote can ever reach.
 * The register does not list those 1445 sites and no second source backs them, so
 * this vote is their only correction path: a threshold nobody reaches is not a
 * cautious default, it is 1445 permanent warnings on mostly-real chargers.
 */

/**
 * A browser voting for the first time.
 *
 * The floor rather than the norm: a vote worth one is the only thing a fresh
 * browser can honestly write, and the rules will not let it claim more.
 */
export const FIRST_VOTE_WEIGHT = 1

/**
 * A browser that first voted at least {@link ESTABLISHED_AFTER_DAYS} ago.
 *
 * Three, not two and not ten. It has to be worth coming back for - answering
 * again a week later should count for more than three strangers arriving at once,
 * because a browser that is still around a week later is the one thing on this
 * page that a fresh profile cannot be. It also has to stay small enough that
 * pre-aged profiles are not a shortcut.
 */
export const ESTABLISHED_VOTE_WEIGHT = 3

/**
 * How long a browser has to have been voting before its votes weigh more.
 *
 * A week, and the number is not arbitrary: Safari's ITP evicts IndexedDB after
 * roughly seven days without interaction, so a browser that still holds its
 * anonymous credential a week on has demonstrably come back. Mirrored in
 * `firestore.rules`, which is where it is actually enforced - this constant only
 * decides what the client *claims*, and a client claiming more than it is owed
 * gets a permission error rather than a heavier vote.
 */
export const ESTABLISHED_AFTER_DAYS = 7

/**
 * When this browser starts claiming {@link ESTABLISHED_VOTE_WEIGHT}: a day later
 * than it is entitled to.
 *
 * The rule compares against the server's clock and the client against its own,
 * and a browser whose clock runs fast would otherwise send a weight the rules
 * refuse - turning a vote into an error message for a reason the driver cannot
 * see or fix. Erring a day late costs an over-eager browser one lighter vote;
 * erring early costs it the vote entirely.
 */
export const ESTABLISHED_CLAIM_AFTER_DAYS = ESTABLISHED_AFTER_DAYS + 1

/**
 * Cleared of the warning: enough people have said the station is there.
 *
 * Six, which reads as two drivers who have voted here before, or six arriving for
 * the first time, or any mix - {@link ESTABLISHED_VOTE_WEIGHT} divides it, and
 * that is what makes it statable as a sentence rather than as a number.
 *
 * Six rather than twelve because twelve does not buy what it looks like it buys.
 * Simulated across four orders of magnitude of traffic, twelve reaches around a
 * third of the sites six does - 68 sites against 169 at a middling vote rate,
 * 234 against 498 at a generous one - while costing a scripted attacker two extra
 * browser profiles it can mint in a loop. The threshold prices honest
 * participation and barely prices the dishonest kind, so it is set for the honest
 * one.
 *
 * Six rather than five because five is one afternoon's work and the spread below
 * is what fixes that, not the score; six simply keeps it divisible.
 */
export const CONFIRMED_AT = 6

/**
 * Reported gone - and deliberately *not* symmetrical with {@link CONFIRMED_AT},
 * because the two mistakes are not the same size.
 *
 * Neither verdict ever removes a pin: a charger the registry lists stays on the
 * map and stays clickable however this reads. What they do differ in is which
 * direction the copy moves. Clearing the warning *removes* the caution a driver
 * would otherwise have read, so a wrong `confirmed` sends somebody to a charger
 * that is not there with nothing on screen to make them doubt it. A wrong
 * `reported-gone` only *sharpens* a warning that was already showing, on a
 * station that keeps its pin, keeps its buttons, and gets corrected by the next
 * driver who finds it working. One error strands a car; the other is visible,
 * reversible and self-healing.
 *
 * So the bar for saying "gone" is a third of the bar for saying "there": one
 * established driver, or three first-timers - and, because of
 * {@link SETTLED_OVER_DAYS}, never fewer than two different browsers on two
 * different days. Simulated, that catches between 1.6 and 2.4 times as many
 * genuinely dead listings as a symmetrical -6 across every traffic level tried,
 * for a worst case of 1.7% of real stations transiently reading "reported gone" -
 * a state the next yes undoes.
 */
export const REPORTED_GONE_AT = -3

/**
 * How many different calendar days the answers have to land on.
 *
 * The cheapest thing to say about a station is a lie told all at once, and this
 * is the only requirement here that time itself has to satisfy. It also buys
 * something for the honest case: a station somebody drove past on two separate
 * days has been seen twice, which is closer to the claim the panel goes on to
 * make than a dozen clicks in one sitting.
 *
 * Two rather than three, and the third day is the one that does not pay for
 * itself. Against a single-site attacker the days are what cost real time, but
 * the second day already forecloses the whole one-sitting attack - a third adds
 * patience an attacker has and honest drivers do not. Against a *national*
 * sweeper a third day is actively worse than useless: the campaign window is also
 * the attacker's working window, so at ten votes an hour a third day cuts the
 * profiles they need from seven to five. Meanwhile it costs the honest side up to
 * 13% of the sites that ever clear.
 *
 * Counted per side. Six points of yes over two days clears the warning; six
 * points of yes in one afternoon does not.
 */
export const SETTLED_OVER_DAYS = 2

export type ConfirmationVote = 1 | -1

/** One browser's answer about one site, as it was actually recorded. */
export interface CastVote {
  readonly vote: ConfirmationVote
  /** {@link FIRST_VOTE_WEIGHT} or {@link ESTABLISHED_VOTE_WEIGHT}, per the rules. */
  readonly weight: number
  /** Server clock, which is what makes {@link SETTLED_OVER_DAYS} mean anything. */
  readonly at: Date
}

/**
 * Which calendar day a vote landed on, in the timezone of the country the map
 * covers.
 *
 * A fixed zone rather than the reader's: two drivers standing at the same charger
 * on the same evening answered on the same day, and whether one of them is on
 * holiday in Tokyo should not turn that into two. Poland is the map, so Poland is
 * the calendar.
 */
const WARSAW_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Warsaw',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

export const dayKey = (at: Date): string => WARSAW_DAY.format(at)

/** How a site currently reads, once the votes are in. */
export type ConfirmationStanding = 'unconfirmed' | 'confirmed' | 'reported-gone'

/** Everything about a site's votes that does not depend on who is asking. */
export interface SiteTally {
  /** Weighted: one established yes moves this by {@link ESTABLISHED_VOTE_WEIGHT}. */
  readonly score: number
  /** Distinct calendar days a yes landed on. */
  readonly confirmDays: number
  /** Distinct calendar days a no landed on. */
  readonly goneDays: number
}

const NOTHING: SiteTally = { score: 0, confirmDays: 0, goneDays: 0 }

/**
 * The votes on one site, reduced to the three numbers the standing is read from.
 *
 * Shared by both backends so that the memory one and Firestore cannot come to
 * different answers about the same votes, and pure so the thresholds can be
 * tested without either.
 */
export const tally = (votes: readonly CastVote[]): SiteTally => {
  const confirmDays = new Set<string>()
  const goneDays = new Set<string>()
  let score = 0

  votes.forEach((cast) => {
    score += cast.vote * cast.weight
    const days = cast.vote === 1 ? confirmDays : goneDays
    days.add(dayKey(cast.at))
  })

  return { score, confirmDays: confirmDays.size, goneDays: goneDays.size }
}

/**
 * Both conditions, per side, and neither on its own is enough.
 *
 * The spread is checked against the days of the *winning* side: a station with
 * six points of yes gathered in one afternoon is not confirmed, and days on which
 * people said no do nothing to help it become so.
 *
 * Recomputed from the whole tally on every read rather than latched, which is
 * what makes a wrong `reported-gone` recoverable: a station that dipped below the
 * bar on a bad week climbs back out when the next drivers say yes, and the panel
 * follows it without anybody having to clear anything.
 */
export const standingFor = (of: SiteTally): ConfirmationStanding => {
  if (of.score >= CONFIRMED_AT && of.confirmDays >= SETTLED_OVER_DAYS) return 'confirmed'
  if (of.score <= REPORTED_GONE_AT && of.goneDays >= SETTLED_OVER_DAYS) return 'reported-gone'
  return 'unconfirmed'
}

export interface SiteConfirmation extends SiteTally {
  /** This browser's own answer, so the panel can show it back and not re-ask. */
  readonly mine: ConfirmationVote | null
}

const UNANSWERED: SiteConfirmation = { ...NOTHING, mine: null }

/**
 * The seam the panel talks to.
 *
 * `load` is separate from `vote` because the score has to be readable without
 * writing anything - a reader who never answers still sees where a station stands
 * - and because the two will have quite different costs once this is Firestore:
 * one is a query, the other a transaction plus an auth round trip.
 */
export interface ConfirmationBackend {
  load(siteId: string): Promise<SiteConfirmation>
  vote(siteId: string, vote: ConfirmationVote): Promise<SiteConfirmation>
}

/**
 * Refused because this browser has answered too many stations in the last hour.
 *
 * Worth its own type rather than folding into the generic failure, because the
 * two want opposite things said. "Try again" is true of a dropped connection and
 * a lie about this one: retrying now fails again, and the honest instruction is
 * to come back later.
 */
export class RateLimited extends Error {
  constructor() {
    super('too many votes in the last hour')
    this.name = 'RateLimited'
  }
}

/**
 * An in-memory backend: the store's starting value, and what the tests drive.
 *
 * It answers honestly for the session and forgets everything on reload, which is
 * why {@link ConfirmationState.persists} is false while it is installed and why
 * the panel asks nobody anything. Something has to be in the seam before the
 * Firestore chunk arrives, and a backend that quietly loses votes is safe only
 * for as long as nobody is invited to cast one.
 *
 * Every vote it records weighs {@link FIRST_VOTE_WEIGHT}, which is not a
 * simplification: a browser whose entire history is forgotten on reload has never
 * voted before by any definition worth having.
 */
export const createMemoryBackend = (): ConfirmationBackend => {
  const byId = new Map<string, CastVote>()

  const standing = (siteId: string): SiteConfirmation => {
    const own = byId.get(siteId)
    return { ...tally(own === undefined ? [] : [own]), mine: own?.vote ?? null }
  }

  return {
    load: async (siteId) => standing(siteId),
    vote: async (siteId, vote) => {
      // One answer per browser per site. Changing your mind replaces the answer
      // rather than counting twice, and re-sending the same one does nothing -
      // which is what makes the threshold count people.
      byId.set(siteId, { vote, weight: FIRST_VOTE_WEIGHT, at: new Date() })
      return standing(siteId)
    },
  }
}

interface ConfirmationState {
  readonly backend: ConfirmationBackend
  /**
   * Whether a vote cast now would outlive the tab - and so whether it is honest
   * to ask for one at all. False until a real backend is installed.
   */
  readonly persists: boolean
  readonly bySite: Readonly<Record<string, SiteConfirmation>>
  /** Sites with a write in flight, so the buttons can be disabled honestly. */
  readonly pending: Readonly<Record<string, true>>
  /** Sites whose last write failed, so the panel can say so rather than lie. */
  readonly failed: Readonly<Record<string, true>>
  /** Sites whose last write was refused by the hourly limit. See {@link RateLimited}. */
  readonly rateLimited: Readonly<Record<string, true>>
  load(siteId: string): Promise<void>
  cast(siteId: string, vote: ConfirmationVote): Promise<void>
  /**
   * Swaps the backend. For tests, and for the Firestore switch-over.
   *
   * `persists` defaults to true because a backend installed on purpose is the
   * real thing; pass false to stand in for one that forgets.
   */
  useBackend(backend: ConfirmationBackend, persists?: boolean): void
}

/**
 * Whether this build was given a Firestore to write to.
 *
 * Read straight from the environment rather than waited for, because
 * {@link ensureFirestoreBackend} resolves a tick or two after first paint and the
 * panel has to decide before that. Buttons that appear late are a layout shift;
 * buttons that appear and then vanish are a promise withdrawn.
 */
const votingConfigured = readFirebaseConfig(import.meta.env) !== undefined

export const useConfirmationStore = create<ConfirmationState>((set, get) => ({
  backend: createMemoryBackend(),
  persists: votingConfigured,
  bySite: {},
  pending: {},
  failed: {},
  rateLimited: {},

  useBackend: (backend, persists = true) => {
    set({ backend, persists, bySite: {}, pending: {}, failed: {}, rateLimited: {} })
  },

  load: async (siteId) => {
    // A failed read is not worth reporting: the panel falls back to zero, which
    // is what an unanswered site reads as anyway.
    try {
      const value = await get().backend.load(siteId)
      set((state) => ({ bySite: { ...state.bySite, [siteId]: value } }))
    } catch {
      /* leave the site unanswered */
    }
  },

  cast: async (siteId, vote) => {
    set((state) => ({
      pending: { ...state.pending, [siteId]: true },
      failed: omit(state.failed, siteId),
      rateLimited: omit(state.rateLimited, siteId),
    }))

    try {
      const value = await get().backend.vote(siteId, vote)
      set((state) => ({
        bySite: { ...state.bySite, [siteId]: value },
        pending: omit(state.pending, siteId),
      }))
    } catch (error) {
      // Nothing optimistic is rolled back because nothing optimistic was
      // written: the score only moves once the backend has confirmed it, so a
      // failed vote leaves the panel showing what is actually recorded.
      const limited = error instanceof RateLimited
      set((state) => ({
        pending: omit(state.pending, siteId),
        failed: limited ? state.failed : { ...state.failed, [siteId]: true },
        rateLimited: limited ? { ...state.rateLimited, [siteId]: true } : state.rateLimited,
      }))
    }
  },
}))

const omit = <T extends Record<string, unknown>>(value: T, key: string): T => {
  const { [key]: _dropped, ...rest } = value
  return rest as T
}

/**
 * Swap the in-memory backend for Firestore, once and only if configured.
 *
 * Dynamically imported, and that is not a micro-optimisation: the Firebase SDK is
 * about a third of the size of this whole bundle, and the overwhelming majority
 * of readers never open a site the pipeline could not confirm. Loading it on
 * first sight of an unconfirmed panel keeps first paint exactly as fast as it was
 * for everybody else.
 *
 * A deployment with no Firebase config keeps the memory backend and never fetches
 * the chunk at all - there is nothing to upgrade to, and `persists` has already
 * told the panel not to ask. That is the state of local development, of the
 * emulator, and of production until `deploy.yml` passes the four `VITE_FIREBASE_*`
 * variables again.
 */
let upgrading: Promise<void> | undefined

/*
 * Named `ensure`, not `use`: this is a plain idempotent side effect, and a `use`
 * prefix would claim it obeys the rules of hooks - callable only at the top level,
 * in a fixed order - which it does not and need not.
 */
export const ensureFirestoreBackend = (): void => {
  if (upgrading !== undefined) return
  const config = readFirebaseConfig(import.meta.env)
  if (config === undefined) return
  upgrading = (async () => {
    try {
      const { createFirestoreBackend } = await import('./firestore')
      useConfirmationStore.setState({ backend: createFirestoreBackend(config), persists: true })
    } catch {
      // A failed chunk load leaves the memory backend in place, which cannot keep
      // a vote past the tab. Withdraw the question rather than collect an answer
      // with nowhere to put it - the explanation above it stands on its own.
      useConfirmationStore.setState({ persists: false })
    }
  })()
}

/** This site's standing, and whether a write is in flight for it. */
export const useSiteConfirmation = (siteId: string) => {
  const value = useConfirmationStore((state) => state.bySite[siteId])
  const pending = useConfirmationStore((state) => state.pending[siteId] === true)
  const failed = useConfirmationStore((state) => state.failed[siteId] === true)
  const rateLimited = useConfirmationStore((state) => state.rateLimited[siteId] === true)
  return { ...(value ?? UNANSWERED), pending, failed, rateLimited }
}
